/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { styled } from "@mui/material/styles";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PropTypes from "prop-types";
import { Input } from "@mui/material";
import MDAvatar from "components/MDAvatar";
import { ErrorMessage, useFormikContext } from "formik";
import MDTypography from "components/MDTypography";
import Danger from "components/Danger";

const ImagePicker = ({
  currentImg = undefined,
  edit = true,
  imageType = "",
  squareImg = false,
  textAdded = "",
  clearable = false,
}) => {
  const [image, _setImage] = useState(undefined);
  const [errorImgType, setErrorImgType] = useState(false);
  const [errorImg, setErrorImg] = useState(false);

  const inputFileRef = useRef(null);
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    const formikValue = values?.[imageType];
    let objectUrlToRevoke = null;

    if (formikValue instanceof File) {
      const newObjectUrl = URL.createObjectURL(formikValue);
      _setImage(newObjectUrl);
      objectUrlToRevoke = newObjectUrl;
    } else if (typeof formikValue === "string" && formikValue) {
      _setImage(formikValue);
    }
    // Fallback to the initial prop if the form value is not set
    else if (formikValue === undefined && typeof currentImg === "string" && currentImg) {
      _setImage(currentImg);
    }
    // The value is cleared (`""` or `null`) or doesn't exist
    else {
      _setImage(undefined);
    }

    // Cleanup function: This runs when the effect re-runs or the component unmounts
    return () => {
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [currentImg, values?.[imageType]]); // Effect dependencies

  const cleanup = () => {
    inputFileRef.current.value = null;
    setFieldValue(imageType, "");
  };

  const handleClear = (e) => {
    e.preventDefault();
    cleanup();
  };

  const handleOnChange = (event) => {
    const newImage = event.target?.files[0];

    if (newImage) {
      const fileSize = newImage.size;

      // Check if the file is an image
      if (!newImage.type.startsWith("image/")) {
        setErrorImgType(true);
        inputFileRef.current.value = "";
        return;
      }

      // Check file size
      if (fileSize > 921600) {
        setErrorImg(true);
        setErrorImgType(false);
      } else {
        setFieldValue(imageType, newImage);
        setErrorImg(false);
        setErrorImgType(false);
      }
    }
  };

  const BigAvatar = styled(Avatar)`
    width: 74px;
    height: 74px;
  `;

  return (
    <label htmlFor={imageType} style={{ display: "block", width: "fit-content" }}>
      {edit && (
        <Input
          accept="image/*"
          id={imageType} // Use imageType as a unique id
          name={imageType} // Also use imageType for the name
          type="file"
          inputRef={inputFileRef}
          onChange={handleOnChange}
          onClick={(e) => {
            e.target.value = null;
          }}
          sx={{ display: "none" }}
        />
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          {image ? (
            <MDAvatar
              src={image}
              alt="profile-image"
              size="xl"
              variant={squareImg ? "square" : null}
              style={{
                height: squareImg ? "12em" : null,
                width: squareImg ? "12em" : null,
              }}
              bgColor="light"
              sx={{ "& img": { height: "100%", background: "rgba(0, 0, 0, 0.1)" } }}
            />
          ) : (
            <BigAvatar
              alt="Change your profile photo"
              variant={squareImg ? "square" : null}
              style={{
                height: squareImg ? "12em" : null,
                width: squareImg ? "12em" : null,
              }}
              src={image}
              imgProps={{
                style: {
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "cover",
                },
              }}
            />
          )}
        </Box>
        {edit && image && clearable && (
          <IconButton
            onClick={handleClear}
            size="small"
            sx={{
              // position: "absolute",
              // top: 4,
              // right: 4,
              backgroundColor: "rgba(255,255,255,0.7)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        {edit && (
          <IconButton color="secondary" aria-label="upload picture" component="span" size="large">
            <FileUploadIcon />
            {textAdded && (
              <MDTypography fontSize="small" marginLeft="5px" fontWeight="regular">
                {textAdded}
              </MDTypography>
            )}
          </IconButton>
        )}
      </Stack>
      {errorImg && <Danger validation={`File size for ${imageType || "logo"} exceeds 900 KB!`} />}
      {errorImgType && <Danger validation="Please select an image file!" />}

      <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
        <ErrorMessage
          name={
            imageType === "photo"
              ? "photo"
              : imageType === "cover_image_url"
              ? "cover_image_url"
              : imageType === "icon"
              ? "icon"
              : imageType === "images"
              ? "images"
              : imageType === "thumbnailHiResURL"
              ? "thumbnailHiResURL"
              : "logo"
          }
        />
      </MDTypography>
    </label>
  );
};

// Setting default props
ImagePicker.propTypes = {
  currentImg: PropTypes.string,
  imageType: PropTypes.string,
  squareImg: PropTypes.bool,
  edit: PropTypes.bool,
  textAdded: PropTypes.string,
  clearable: PropTypes.bool,
};

export default ImagePicker;
